import useIsGroupAdmin from '@hooks/useIsGroupAdmin';
import { setClaims } from '@modules/api';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import { AccessRole, UserWithClaims } from '@synergetx/puppet-interview-types';
import { memo } from 'react';
import { toast } from 'react-toastify';

interface Props {
    rowData: UserWithClaims;
    refreshUsers: () => Promise<void> | void;
}

const AdminPermissionSelect = ({ rowData, refreshUsers }: Props) => {
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;
    const isGroupAdmin = useIsGroupAdmin();

    const handleChange = (event: SelectChangeEvent) => {
        const optionChosen = event.target.value as AccessRole | AccessRole;

        if (!groupId) return toast.error('No group selected');

        toast.promise(
            setClaims(groupId, optionChosen, rowData.id).then(() => {
                refreshUsers();
            }),
            {
                error: 'Error updating user survey permissions',
                pending: `Updating survey permissions...`,
                success: 'Success!',
            },
        );
    };

    return (
        <Select
            size="small"
            displayEmpty
            onChange={handleChange}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            value={rowData.claims[`group_${groupId}`] || AccessRole.NONE}
            sx={{
                minWidth: 140,
                '& .MuiSelect-select': {
                    py: 1,
                },
            }}
        >
            <MenuItem value={AccessRole.NONE}>None</MenuItem>
            <MenuItem value={AccessRole.ASSESSOR}>Assessor</MenuItem>
            <MenuItem value={AccessRole.SUPERVISOR}>Project Lead</MenuItem>
            {isGroupAdmin && <MenuItem value={AccessRole.ADMIN}>Admin</MenuItem>}
        </Select>
    );
};

export default memo(AdminPermissionSelect);
